import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, DownloadSimple } from "@phosphor-icons/react";

const BADGE = {
  autorizada: "bg-green-600 text-white",
  rejeitada: "bg-red-600 text-white",
  cancelada: "bg-zinc-800 text-white",
  aguardando_processamento: "bg-yellow-500 text-black",
  em_digitacao: "bg-blue-500 text-white",
  denegada: "bg-red-800 text-white",
};

/**
 * Botão inline para emissão de NF-e a partir da tela do pedido.
 * - Se ainda não há NF-e: abre modal com % de redução e chama /fiscal/nfe/emit-from-order
 * - Se já há NF-e: mostra o status + link "Ver no Fiscal" + botão de DANFE
 */
export default function EmitNfeButton({ order }) {
  const navigate = useNavigate();
  const [nfe, setNfe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [percentage, setPercentage] = useState(100);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/fiscal/nfe", { params: { limit: 20 } });
        if (!alive) return;
        const list = (data.items || []).filter((n) => n.order_id === order.id);
        list.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        setNfe(list[0] || null);
      } catch (e) {
        // Silencioso na UI (pode ser 403 se o usuário não tiver perm `fiscal`),
        // mas deixa rastro no console para investigar falhas inesperadas.
        console.debug("[EmitNfeButton] fetch nfes falhou:", e?.response?.status || e?.message);
      }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [order.id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const factor = Math.max(0.01, Math.min(1.0, percentage / 100));
      const { data } = await api.post("/fiscal/nfe/emit-from-order", {
        order_id: order.id,
        apply_percentage: factor,
      });
      if (data.already_exists) toast.warning("Este pedido já possui NF-e");
      else toast.success(`NF-e criada (${data.nfe?.status || "processando"})`);
      setNfe(data.nfe);
      setShowModal(false);
    } catch (e) {
      toast.error("Falha: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadDanfe = async () => {
    try {
      const res = await api.get(`/fiscal/nfe/${nfe.id}/danfe.pdf`, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `danfe_${nfe.number || nfe.id}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error("DANFE indisponível — verifique se a NF-e foi autorizada");
    }
  };

  if (loading) return null;

  // Se já existe NF-e ativa
  if (nfe && ["autorizada", "aguardando_processamento", "em_digitacao"].includes(nfe.status)) {
    return (
      <div className="flex items-center gap-2" data-testid="order-nfe-existing">
        <span className={`text-[10px] font-mono px-2 py-1 uppercase ${BADGE[nfe.status] || "bg-zinc-200"}`}>
          NF-e {nfe.number || "…"} · {(nfe.status || "").replace(/_/g, " ")}
        </span>
        {nfe.status === "autorizada" && (
          <button onClick={downloadDanfe} className="p-2 border border-black hover:bg-black hover:text-white" title="Baixar DANFE" data-testid="order-nfe-download">
            <DownloadSimple size={14} weight="bold" />
          </button>
        )}
        <button onClick={() => navigate("/fiscal/history")} className="text-xs underline text-zinc-600 hover:text-black" data-testid="order-nfe-history-link">
          Histórico
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => { setPercentage(100); setShowModal(true); }}
        className="px-3 py-2 border border-black bg-white hover:bg-black hover:text-white text-sm font-bold flex items-center gap-2"
        data-testid="order-emit-nfe-btn"
      >
        <Receipt size={16} weight="bold" />
        {nfe ? "Reemitir NF-e" : "Emitir NF-e"}
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !submitting && setShowModal(false)}>
          <div className="bg-white border border-black w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="order-emit-nfe-modal">
            <div className="border-b border-black px-5 py-4">
              <div className="overline text-zinc-500 text-xs">Emissão de NF-e</div>
              <div className="font-head font-black text-lg">Pedido #{order.bling_number}</div>
              <div className="text-xs text-zinc-500 mt-1">{order.customer}</div>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wide text-zinc-600 mb-2">Valor a emitir na nota</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number" min={1} max={100} step={1}
                    value={percentage}
                    onChange={(e) => setPercentage(Number(e.target.value))}
                    className="border border-black px-3 py-2 w-24 font-mono text-right"
                    data-testid="order-emit-nfe-percentage"
                  />
                  <span className="text-sm text-zinc-500">% do valor unitário</span>
                </div>
              </div>
              <div className="text-xs bg-zinc-50 border border-zinc-200 p-2 text-zinc-600">
                O pedido no PopCopos permanece intacto. Apenas a nota vai com o valor reduzido.
              </div>
            </div>
            <div className="border-t border-black px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setShowModal(false)} disabled={submitting} className="px-4 py-2 text-sm border border-black hover:bg-zinc-100" data-testid="order-emit-nfe-cancel">Cancelar</button>
              <button onClick={submit} disabled={submitting} className="px-4 py-2 text-sm bg-black text-white font-bold hover:bg-zinc-800 disabled:opacity-50" data-testid="order-emit-nfe-confirm">
                {submitting ? "Enviando ao Bling…" : "Emitir NF-e"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
